import { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Package,
  Plus,
  Trash2,
  Search,
  Loader2,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProductManagement() {
  const { user, loading: authLoading } = useAuth();
  const { companyId, selectedCompany } = useCompany();
  
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [newProductName, setNewProductName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  const fetchProducts = useCallback(async () => {
    if (!companyId) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/products?company_id=${companyId}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setProducts(data.products || []);
      }
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (!authLoading && user && companyId) {
      fetchProducts();
    }
  }, [authLoading, user, companyId, fetchProducts]);

  const handleAddProduct = async (e) => {
    e.preventDefault();
    
    if (!newProductName.trim()) {
      toast.error('Please enter a product name');
      return;
    }
    
    if (!companyId) {
      toast.error('Please select a company first');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/products?company_id=${companyId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: newProductName.trim() })
      });

      if (response.ok) {
        toast.success('Product added successfully');
        setNewProductName('');
        fetchProducts();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add product');
      }
    } catch (error) {
      console.error('Failed to add product:', error);
      toast.error('Failed to add product');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteProduct = async (productId, productName) => {
    setDeletingId(productId);
    try {
      const response = await fetch(`${API_URL}/api/products/${productId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success(`"${productName}" deleted`);
        setShowDeleteConfirm(null);
        fetchProducts();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete product');
      }
    } catch (error) {
      console.error('Failed to delete product:', error);
      toast.error('Failed to delete product');
    } finally {
      setDeletingId(null);
    }
  };

  // Filter products by search query
  const filteredProducts = products.filter(p => 
    p.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="loading-state">
          <div className="loading-spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-content" data-testid="product-management-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Product Management</h1>
            <p className="text-slate-500 mt-1">
              Products for <span className="font-medium text-slate-700">{selectedCompany?.name}</span> ({products.length} products)
            </p>
          </div>
        </div>

        {/* Add New Product Form */}
        <div className="bg-white border border-slate-200 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-slate-900 mb-3">Add New Product</h3>
          <form onSubmit={handleAddProduct} className="flex gap-3">
            <div className="flex-1">
              <Input
                value={newProductName}
                onChange={(e) => setNewProductName(e.target.value)}
                placeholder="Enter product name..."
                className="h-11"
                data-testid="new-product-input"
              />
            </div>
            <Button 
              type="submit" 
              disabled={submitting || !newProductName.trim()}
              className="h-11 gap-2"
              data-testid="add-product-btn"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Plus className="w-4 h-4" />
              )}
              Add Product
            </Button>
          </form>
        </div>

        {/* Search */}
        <div className="bg-white border border-slate-200 rounded-lg p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search products..."
              className="pl-10 h-11"
              data-testid="search-input"
            />
          </div>
        </div>

        {/* Products List */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          {filteredProducts.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500">
                {searchQuery ? 'No products match your search' : 'No products found'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredProducts.map((product) => (
                <div 
                  key={product.product_id} 
                  className="flex items-center justify-between p-4 hover:bg-slate-50"
                  data-testid={`product-row-${product.product_id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
                      <Package className="w-4 h-4 text-slate-600" />
                    </div>
                    <span className="font-medium text-slate-900">{product.name}</span>
                  </div>
                  
                  {showDeleteConfirm === product.product_id ? (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-red-600 mr-2">Delete?</span>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteProduct(product.product_id, product.name)}
                        disabled={deletingId === product.product_id}
                        data-testid={`confirm-delete-${product.product_id}`}
                      >
                        {deletingId === product.product_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          'Yes'
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowDeleteConfirm(null)}
                      >
                        No
                      </Button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowDeleteConfirm(product.product_id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-btn-${product.product_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info Note */}
        <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="text-sm text-amber-800 font-medium">Note</p>
              <p className="text-sm text-amber-700 mt-1">
                Changes to this list will affect the product dropdown in the "New Production Entry" form. 
                Deleting a product will not affect existing tickets that use that product name.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
